import { AiChat, AiChatEvent } from '@qtek/shared/models';

export interface AiChatState {
  chats: AiChat[];
  eventsByChatId: Record<string, AiChatEvent[]>;
  createdChatIdByMysid: Record<string, string | null>;
}

export const aiChatInitialState: AiChatState = {
  chats: [],
  eventsByChatId: {},
  createdChatIdByMysid: {},
};

const MAX_PREVIEW_LENGTH = 55;

export function toTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return 0;
}

/** ISO instant or epoch millis -> epoch millis; anything absent/unparseable -> null. */
function toEpochMillis(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

/** Wire shape of a Chat as it arrives over the WS; field names vary by BE version. */
export interface RawChat {
  id?: string;
  _id?: string;
  title?: string;
  name?: string;
  subject?: string;
  preview?: string;
  summary?: string;
  snippet?: string;
  updatedAt?: number | string;
  _uts?: number | string;
  uts?: number | string;
  updated?: number | string;
  pinnedAt?: number | string | null;
  deletedAt?: number | string | null;
}

export function normalizeChat(raw: RawChat | null | undefined): AiChat | null {
  if (!raw) return null;
  const id = raw?.id ?? raw?._id;
  if (!id) return null;
  const title = raw?.title ?? raw?.name ?? raw?.subject;
  const preview =
    typeof raw?.preview === 'string'
      ? raw.preview
      : typeof raw?.summary === 'string'
        ? raw.summary
        : typeof raw?.snippet === 'string'
          ? raw.snippet
          : undefined;
  const updatedAtSource =
    raw?.updatedAt ?? raw?._uts ?? raw?.uts ?? raw?.updated;
  const updatedAt = toEpochMillis(updatedAtSource) ?? undefined;
  // pinnedAt/deletedAt must always be explicit keys: the BE omits them when null and chats are
  // merged by spreading, so a missing key would keep a stale pin after an unpin.
  const pinnedAt = toEpochMillis(raw?.pinnedAt);
  const deletedAt = toEpochMillis(raw?.deletedAt);
  return { id, title, preview, updatedAt, pinnedAt, deletedAt };
}

export function sortChats(list: AiChat[]): AiChat[] {
  return [...(list ?? [])].sort((a, b) => {
    const at = toTimestamp(
      a.updatedAt ??
        (a as { createdAt?: unknown }).createdAt ??
        (a as { _uts?: unknown })._uts
    );
    const bt = toTimestamp(
      b.updatedAt ??
        (b as { createdAt?: unknown }).createdAt ??
        (b as { _uts?: unknown })._uts
    );
    return bt - at;
  });
}

function upsertChat(list: AiChat[], chat: AiChat): AiChat[] {
  const i = list.findIndex(x => x.id === chat.id);
  if (i >= 0) {
    const next = list.slice();
    next[i] = { ...next[i], ...chat };
    return next;
  }
  return [...list, chat];
}

function mergeChats(existing: AiChat[], incoming: AiChat[]): AiChat[] {
  if (!existing?.length) return sortChats(incoming ?? []);
  if (!incoming?.length) return sortChats(existing ?? []);

  const next = existing.slice();
  for (const chat of incoming) {
    if (!chat?.id) continue;
    const idx = next.findIndex(item => item.id === chat.id);
    if (idx >= 0) {
      next[idx] = { ...next[idx], ...chat };
    } else {
      next.push(chat);
    }
  }
  return sortChats(next);
}

function eventKey(e: AiChatEvent): string {
  return `${e.chatId}|${e.ts}|${(e as { src?: string }).src ?? ''}`;
}

function dedupeAppendEvents(
  existing: AiChatEvent[],
  incoming: AiChatEvent[]
): AiChatEvent[] {
  const byKey = new Map(existing.map(e => [eventKey(e), e]));
  for (const e of incoming) {
    const k = eventKey(e);
    byKey.set(k, { ...(byKey.get(k) || {}), ...e });
  }

  return Array.from(byKey.values()).sort((a, b) => a.ts - b.ts);
}

export function applyChatsQuery(
  state: AiChatState,
  chats: AiChat[]
): Partial<AiChatState> {
  return { chats: mergeChats(state.chats, chats ?? []) };
}

export function applyChatCreated(
  state: AiChatState,
  chat: AiChat,
  mysid?: string
): Partial<AiChatState> {
  return {
    chats: upsertChat(state.chats, chat),
    createdChatIdByMysid: mysid
      ? { ...state.createdChatIdByMysid, [mysid]: chat.id }
      : state.createdChatIdByMysid,
  };
}

export function applyChatUpdated(
  state: AiChatState,
  chat: AiChat
): Partial<AiChatState> {
  return { chats: upsertChat(state.chats, chat) };
}

/**
 * Forgets a chat: list row, cached transcript, and its record as a panel-created chat. The last
 * matters because `createdChatIdByMysid` is the only place a fresh panel chat's id is kept —
 * left behind, the next prompt is written against a deleted chat ("Chat not found").
 */
export function removeChat(
  state: AiChatState,
  id: string
): Partial<AiChatState> {
  if (!id) return {};

  const eventsByChatId = { ...state.eventsByChatId };
  delete eventsByChatId[id];

  const createdChatIdByMysid = Object.fromEntries(
    Object.entries(state.createdChatIdByMysid).filter(
      ([, chatId]) => chatId !== id
    )
  );

  return {
    chats: (state.chats ?? []).filter(chat => chat.id !== id),
    eventsByChatId,
    createdChatIdByMysid,
  };
}

export function applyChatEvents(
  state: AiChatState,
  events: AiChatEvent[]
): Partial<AiChatState> {
  const next = { ...state.eventsByChatId };
  for (const e of events ?? []) {
    if (!e?.chatId) continue;
    next[e.chatId] = dedupeAppendEvents(next[e.chatId] ?? [], [e]);
  }
  return { eventsByChatId: next };
}

export function clearCreatedChat(
  state: AiChatState,
  mysid: string
): Partial<AiChatState> {
  if (!mysid) return {};

  const createdChatIdByMysid = { ...state.createdChatIdByMysid };
  delete createdChatIdByMysid[mysid];
  return { createdChatIdByMysid };
}

function firstMessagePreview(events: AiChatEvent[] | undefined): string {
  if (!events?.length) return '';
  const text = (events[0]?.data ?? '').trim();
  if (text.length <= MAX_PREVIEW_LENGTH) return text;
  return text.slice(0, MAX_PREVIEW_LENGTH).trim() + '...';
}

/** Chats with preview set from API or derived from the first message. */
export function withPreviews(
  chats: AiChat[],
  eventsByChatId: Record<string, AiChatEvent[]>
): AiChat[] {
  return (chats ?? []).map(chat => ({
    ...chat,
    preview: chat.preview ?? firstMessagePreview(eventsByChatId?.[chat.id]),
  }));
}

/** Pinned chats, most recently pinned first. */
export function selectPinned(chats: AiChat[]): AiChat[] {
  return chats
    .filter(chat => chat.pinnedAt != null)
    .sort((a, b) => toTimestamp(b.pinnedAt) - toTimestamp(a.pinnedAt));
}

/** Unpinned chats, most recently updated first. */
export function selectRecent(chats: AiChat[]): AiChat[] {
  return sortChats(chats.filter(chat => chat.pinnedAt == null));
}
